/**
 * Question Utilities Module
 * Shared utility functions used across question modules
 * Extracted to break circular dependencies
 */

/**
 * Result of shuffling question options
 */
export interface ShuffleResult {
  /** Shuffled options array */
  shuffledOptions: string[];
  /** New index of the correct answer after shuffling */
  shuffledAnswerIndex: number;
  /** Original indices mapping */
  originalIndices: number[];
}

/**
 * Helper function to generate option labels (A, B, C... Z, AA, AB, etc)
 * @param index - The option index
 * @returns The option label
 */
export function generateOptionLabel(index: number): string {
  // For indices 0-25, return simple A-Z
  if (index < 26) {
    return String.fromCharCode(65 + index); // ASCII 65 = 'A'
  }
  
  // For indices ≥26, use AA, AB, AC pattern (base-26 conversion)
  let label = '';
  let value = index;
  
  // Convert to base-26 (A-Z) with leading digit
  do {
    const remainder = value % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor(value / 26) - 1; // Adjust for AA starting after Z
  } while (value >= 0);
  
  return label;
}

/**
 * Shuffle question options using Fisher-Yates algorithm
 * @param options - The original options array
 * @param correctAnswerIndex - The index of the correct answer
 * @returns Object with shuffled options, new answer index, and original indices
 */
export function shuffleQuestionOptions(options: string[], correctAnswerIndex: number): ShuffleResult {
  // Create array of indices [0, 1, 2, ...]
  const indices = options.map((_, i) => i);
  
  // Fisher-Yates shuffle algorithm
  for (let i = indices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = indices[i];
    const swapValue = indices[j];
    if (temp !== undefined && swapValue !== undefined) {
      indices[i] = swapValue;
      indices[j] = temp;
    }
  }
  
  // Build shuffled options array and find new position of correct answer
  const shuffledOptions = indices.map(idx => options[idx] ?? '').filter(opt => opt !== '');
  const shuffledAnswerIndex = indices.indexOf(correctAnswerIndex);
  
  return {
    shuffledOptions,
    shuffledAnswerIndex,
    originalIndices: indices
  };
}
